import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import InventoryTable from './InventoryTable';
import { ProductFilter, Product } from './ProductFilter';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StoreInvitation from './StoreInvitation';
import { useLazyQuery } from '@apollo/client';

import { GET_INTEGRATIONS_BY_TYPE } from '~/services/store';

interface InventoryProps {
  showStoreInvitation: boolean;
  onInvitationStateChange: (show: boolean) => void;
}

const Store: React.FC<InventoryProps> = ({
  showStoreInvitation,
  onInvitationStateChange,
}) => {
  const [t] = useTranslation('store');
  const [store, setStore] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [refreshInventory, setRefreshInventory] = useState<(() => void) | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  const { agentId } = useParams();

  const [getIntegrations, { loading, error, data: integrationsData }] =
    useLazyQuery(GET_INTEGRATIONS_BY_TYPE, {
      variables: { data: { agent_id: agentId, type_integration: 'ECOMMERCE' } },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        // Check if we have any ECOMMERCE integrations
        const integrationData = data?.get_integrations_by_type?.data;
        const hasIntegrations =
          integrationData &&
          typeof integrationData === 'object' &&
          Object.keys(integrationData).length > 0;

        if (hasIntegrations) {
          onInvitationStateChange(false);
        }
      },
    });

  // Function to determine if invitation should be hidden
  const shouldHideInvitation = (
    storeType: string,
    hasProducts: boolean,
    hasIntegrations: boolean,
  ) => {
    return (
      hasIntegrations || (storeType && storeType.length > 0) || hasProducts
    );
  };

  useEffect(() => {
    if (agentId) {
      getIntegrations();
    }
  }, [agentId, getIntegrations]);

  const handleStoreChange = (storeValue: string) => {
    setStore(storeValue);

    // Check if we should hide invitation based on store type
    const integrationData = integrationsData?.get_integrations_by_type?.data;
    const hasIntegrations =
      integrationData &&
      typeof integrationData === 'object' &&
      Object.keys(integrationData).length > 0;

    if (shouldHideInvitation(storeValue, false, hasIntegrations)) {
      onInvitationStateChange(false);
    }
  };

  // New function to handle when products are detected
  const handleProductsDetected = (productCount: number) => {
    const integrationData = integrationsData?.get_integrations_by_type?.data;
    const hasIntegrations =
      integrationData &&
      typeof integrationData === 'object' &&
      Object.keys(integrationData).length > 0;
    const hasProducts = productCount > 0;

    if (shouldHideInvitation(store, hasProducts, hasIntegrations)) {
      onInvitationStateChange(false);
    }
  };

  const handleAddProduct = () => {
    // Hide the invitation screen when adding a product
    onInvitationStateChange(false);

    if (showStoreInvitation) {
      navigate(`/agent/${agentId}/store/add?first=true`);
    } else {
      navigate(`/agent/${agentId}/store/add`);
    }
  };

  const handleSearchResults = (products: Product[], searchActive: boolean) => {
    setSearchResults(products);
    setIsSearchActive(searchActive);
  };

  const handleClearResults = () => {
    setSearchResults([]);
    setIsSearchActive(false);
  };

  const handleRefreshInventory = async () => {
    if (refreshInventory) {
      setIsRefreshing(true);
      try {
        await refreshInventory();
        console.log('Inventario actualizado exitosamente');
      } catch (error) {
        console.error('Error al actualizar inventario:', error);
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const handleSetRefreshFunction = (refreshFn: () => void) => {
    setRefreshInventory(() => refreshFn);
  };

  // Show loading skeleton while determining what to display
  if (loading) {
    return (
      <div className="mx-auto mb-24 flex h-full w-full max-w-[1200px] flex-col p-6">
        <div className="animate-pulse">
          {/* Header skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-base-300 rounded-md w-64 mb-2"></div>
            <div className="h-4 bg-base-300 rounded-md w-80"></div>
          </div>

          {/* Search bar skeleton */}
          <div className="mb-6">
            <div className="h-12 bg-base-300 rounded-lg w-full max-w-md"></div>
          </div>

          {/* Table skeleton */}
          <div className="space-y-4">
            <div className="h-12 bg-base-300 rounded-lg w-full"></div>
            <div className="h-12 bg-base-300 rounded-lg w-full"></div>
            <div className="h-12 bg-base-300 rounded-lg w-full"></div>
            <div className="h-12 bg-base-300 rounded-lg w-full"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto mb-24 flex h-full w-full max-w-[1200px] flex-col p-6">
      {showStoreInvitation ? (
        /* Show only the invitation message covering the full content area */
        <div className="flex min-h-[60vh] items-center justify-center">
          <StoreInvitation onAddProduct={handleAddProduct} />
        </div>
      ) : (
        /* Show the normal header, filter and table when invitation is hidden */
        <>
          <div className="mb-8">
            <h1 className="text-3xl font-bold">{t('store_inventory')}</h1>
            <p className="text-base-content">
              {t('store_subtitle', 'Manage your product inventory')}
            </p>
          </div>

          {/* Search and Add Product Button Row */}
          <div className="mb-6">
            {/* Product Filter for Vector Search */}
            {agentId && (
              <ProductFilter
                agentId={agentId}
                onSearchResults={handleSearchResults}
                onClearResults={handleClearResults}
                showAddButton={store === 'tbitStore'}
                onAddProduct={handleAddProduct}
                onRefresh={handleRefreshInventory}
                isRefreshing={isRefreshing}
              />
            )}
          </div>

          {/* inventory items table */}
          <InventoryTable
            onStoreChange={handleStoreChange}
            onProductsDetected={handleProductsDetected}
            searchResults={isSearchActive ? searchResults : undefined}
            isSearchActive={isSearchActive}
            onRefreshInventory={handleSetRefreshFunction}
          />
        </>
      )}
    </div>
  );
};

export default Store;
